import { signIn as amplifySignIn, fetchAuthSession, fetchUserAttributes } from 'aws-amplify/auth';
import PropTypes from 'prop-types';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { FormattedMessage, injectIntl } from 'react-intl';
import {
  Button, Form, FormFeedback, FormGroup, FormText, Input, Label,
} from 'reactstrap';
import isEmail from 'validator/es/lib/isEmail';
import isLength from 'validator/es/lib/isLength';

import { isDevelopmentAuth } from 'src/auth/dev';
import FlashesStore from 'src/stores/FlashesStore';
import { withTracingAsync } from 'src/tracing';
import { AUTHENTICATION_FAILURE_REDIRECT_REASON } from 'src/util/constants';
import { setLoggedInUserDetails } from 'src/util/intercom';
import logger from 'src/util/logger';

import AuthContainer, { AUTH_TYPE_LOGIN } from '../AuthContainer';

/**
 * Description
 * @param {any} username
 * @param {any} password
 * @returns {boolean} - sign in true or false
 */
async function signInRaw(username, password) {
  const mutationName = 'authLogin';

  try {
    await amplifySignIn({
      username,
      password,
      options: { authFlowType: 'USER_PASSWORD_AUTH' },
    });
    const attributes = await fetchUserAttributes();
    setLoggedInUserDetails(attributes);
    return true;
  } catch (error) {
    FlashesStore.flash(FlashesStore.ERROR, error.message, mutationName);
    logger.log({ message: 'error signing in', error });
    return false;
  }
}

/**
 * Description
 * @param {any} username
 * @param {any} password
 * @returns {any} - promise
 */
async function signIn(username, password) {
  return withTracingAsync('signin', signInRaw, username, password);
}

class AuthLogin extends React.Component {
  static mutationName = 'authLogin';

  constructor(props) {
    super(props);

    this.state = {
      checkingSession: true,
      email: '',
      emailValid: null,
      password: '',
      passwordValid: null,
      processing: false,
    };

    this.mounted = false;
  }

  async componentDidMount() {
    this.mounted = true;
    if (isDevelopmentAuth()) {
      window.location.replace('/__dev/login');
      return;
    }

    const { intl, router } = this.props;
    try {
      const session = await fetchAuthSession({ forceRefresh: true });
      // Guard post-await side effects: if the user navigated away before
      // fetchAuthSession() settled, avoid flashing or redirecting on an
      // unrelated page.
      if (!this.mounted) {
        return;
      }
      if (session?.tokens) {
        const msg = intl.formatMessage({
          id: 'auth.auth_login.already_logged_in.redirect_message',
          defaultMessage: "Redirecting as you're already logged in ...",
        });
        FlashesStore.flash(FlashesStore.SUCCESS, msg);
        router.push('/properties');
        // Keep checkingSession true so the form never renders during redirect.
        return;
      }
    } catch (error) {
      // fetchAuthSession() resolves with undefined tokens when unauthenticated,
      // so reaching here is a genuine session/token failure, not "no session".
      logger.error({ message: 'session check failed', error });
      // Guard post-await side effects against a late rejection after unmount.
      if (!this.mounted) {
        return;
      }
    }
    // Guard against a late resolve after the component has unmounted (e.g. the
    // user navigated away before fetchAuthSession() settled).
    if (this.mounted) {
      this.publishAuthenticationFailureFlash();
      this.setState({ checkingSession: false });
    }
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  publishAuthenticationFailureFlash = () => {
    const { intl, match } = this.props;
    const { location } = match;
    const searchParams = new URLSearchParams(location.search || '');

    if (searchParams.get('reason') !== AUTHENTICATION_FAILURE_REDIRECT_REASON) {
      return;
    }

    const message = intl.formatMessage({
      id: 'auth.auth_login.flash.authentication_failure.message',
      defaultMessage: 'Authentication failed.',
    });
    FlashesStore.flash(FlashesStore.ERROR, message);

    searchParams.delete('reason');
    const search = searchParams.toString();
    const pathname = location.pathname || window.location.pathname;
    const hash = location.hash || window.location.hash || '';
    window.history.replaceState(
      window.history.state,
      '',
      `${pathname}${search ? `?${search}` : ''}${hash}`,
    );
  };

  redirect = () => {
    const { match } = this.props;
    const { location } = match;
    const { search } = location;

    const regex = /[?&]redirect(=([^&#]*)|&|#|$)/i;
    const results = regex.exec(search);

    if (!results || !results[2]) {
      return '/';
    }

    return decodeURIComponent(results[2].replace(/\+/g, ' '));
  };

  handleEmailChange = (e) => {
    const { value: email } = e.target;
    const emailValid = isEmail(email);

    this.setState({ email, emailValid });
  };

  handlePasswordChange = (e) => {
    const { value: password } = e.target;
    const passwordValid = isLength(password, { min: 8 });

    this.setState({ password, passwordValid });
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    const { intl, router } = this.props;
    const { email, password, processing } = this.state;

    FlashesStore.reset();

    if (processing) {
      const msgInprogress = intl.formatMessage({ id: 'auth.auth_login.form.state.invalid_still_processing', defaultMessage: 'We are still processing your request...' });
      FlashesStore.flash(FlashesStore.INFO, msgInprogress, AuthLogin.mutationName);
      return;
    }

    if (!this.isValid()) {
      const msgInvalidData = intl.formatMessage({ id: 'auth.auth_login.form.state.invalid_data', defaultMessage: 'Form data not valid. Please see below.' });
      FlashesStore.flash(FlashesStore.ERROR, msgInvalidData, AuthLogin.mutationName);
      return;
    }

    try {
      this.setState({ processing: true });

      const login = await signIn(email, password);

      this.setState({ processing: false });

      if (login) {
        const redirect = this.redirect();
        const msgSuccess = (
          <FormattedMessage
            id="auth.auth_login.form.response_message.success"
            defaultMessage="Login succeeded, redirecting to {target}"
            values={{
              target: redirect,
            }}
          />
        );
        FlashesStore.flash(FlashesStore.SUCCESS, msgSuccess, AuthLogin.mutationName);
        router.push(redirect);
      }
    } catch (error) {
      this.setState({ processing: false });

      FlashesStore.flash(FlashesStore.ERROR, error, AuthLogin.mutationName);
      logger.log({ error });
    }
  };

  isValid = () => {
    const { emailValid, passwordValid } = this.state;

    return emailValid && passwordValid;
  };

  render() {
    const {
      checkingSession, email, emailValid, password, passwordValid, processing,
    } = this.state;
    const { intl } = this.props;

    // While the session check is in flight (and during a redirect for an
    // already-authenticated user), render nothing so the login form never
    // flashes on screen before the redirect resolves.
    if (checkingSession) {
      return null;
    }

    const pageTitle = intl.formatMessage({ id: 'auth.auth_login.page.title', defaultMessage: 'Log in to Enosi' });
    const formTitle = intl.formatMessage({ id: 'auth.auth_login.form.title', defaultMessage: 'Log in' });
    const emailLabel = intl.formatMessage({ id: 'auth.auth_login.form.email_address.label', defaultMessage: 'Email address' });
    const emailPlaceholder = intl.formatMessage({ id: 'auth.auth_login.form.email.placeholder', defaultMessage: 'Enter email address' });
    const emailFeedbackInvalid = intl.formatMessage({ id: 'auth.auth_login.form.email.form_feedback.invalid', defaultMessage: 'Invalid email address' });
    const emailHelpText = intl.formatMessage({ id: 'auth.auth_login.form.email.help_text', defaultMessage: 'Email address associated with your Enosi account' });
    const passwordLabel = intl.formatMessage({ id: 'auth.auth_login.form.password.label', defaultMessage: 'Password' });
    const passwordPlaceholder = intl.formatMessage({ id: 'auth.auth_login.form.password.placeholder', defaultMessage: 'Enter password' });
    const submitLabel = intl.formatMessage({ id: 'auth.auth_login.form.submit.label', defaultMessage: 'Log in' });

    return (
      <>
        <Helmet>
          <meta charSet="utf-8" />
          <title>{pageTitle}</title>
        </Helmet>

        <AuthContainer type={AUTH_TYPE_LOGIN} title={formTitle}>
          <Form onSubmit={this.handleSubmit}>
            <FormGroup>
              <Label htmlFor="email">{emailLabel}</Label>
              <Input
                type="email"
                id="email"
                aria-describedby="email"
                placeholder={emailPlaceholder}
                onChange={this.handleEmailChange}
                value={email}
                valid={emailValid}
                invalid={emailValid !== null && !emailValid}
                disabled={processing}
              />
              <FormFeedback>{emailFeedbackInvalid}</FormFeedback>
              <FormText>{emailHelpText}</FormText>
            </FormGroup>
            <FormGroup>
              <Label htmlFor="password">{passwordLabel}</Label>
              <Input
                type="password"
                id="password"
                aria-describedby="password"
                placeholder={passwordPlaceholder}
                onChange={this.handlePasswordChange}
                value={password}
                valid={passwordValid}
                invalid={passwordValid !== null && !passwordValid}
                disabled={processing}
              />
              <FormFeedback>
                <FormattedMessage
                  id="auth.auth_login.form.password.form_feedback.invalid"
                  defaultMessage="Password must be at least 8 characters long, with uppercase, lowercase, number and one of:{specialCharacters}"
                  values={{
                    specialCharacters:
                      // eslint-disable-next-line react/jsx-indent
                      <>
                        <br />
                        <code className="code">
                          {'^ $ * . [ ] { } ( ) ? " ! @ # % & / \\ , < > \' : ; | _ ~ `'}
                        </code>
                      </>,
                  }}
                />
              </FormFeedback>
            </FormGroup>
            <Button type="submit" color="primary" disabled={processing}>
              {submitLabel}
            </Button>
          </Form>
        </AuthContainer>
      </>
    );
  }
}

AuthLogin.propTypes = {
  intl: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  match: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  router: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
};

export default injectIntl(AuthLogin);
